import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

import type { DraftField, WatermarkDraft } from '../watermark-form';

interface Props {
  draft: WatermarkDraft;
  errors: Partial<Record<DraftField, string>>;
  disabled: boolean;
  onChange: (patch: Partial<WatermarkDraft>) => void;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** Estilo de la marca en una grilla compacta: opacidad, tamaño, rotación, color y mosaico. */
export function StyleFields({ draft, errors, disabled, onChange }: Props): ReactElement {
  const num = (
    field: 'opacityPct' | 'fontSizePt' | 'rotationDeg',
    label: string,
    min: number,
    max: number,
  ): ReactElement => (
    <label className="fld">
      <span className="fld-lab">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        className="selector fw"
        min={min}
        max={max}
        value={draft[field]}
        disabled={disabled}
        aria-invalid={errors[field] !== undefined}
        onChange={(e) => onChange({ [field]: e.target.value })}
      />
      {errors[field] && <span className="fld-err">{errors[field]}</span>}
    </label>
  );

  return (
    <fieldset className="fld wmstyle">
      <legend className="fld-lab">Estilo</legend>
      <div className="wmstyle-grid">
        {num('opacityPct', 'Opacidad (%)', 5, 100)}
        {num('fontSizePt', 'Tamaño (pt)', 6, 200)}
        {num('rotationDeg', 'Rotación (°)', -90, 90)}
        <label className="fld">
          <span className="fld-lab">Color</span>
          <span className="wmcolor">
            <input
              type="color"
              aria-label="Elegir color"
              value={HEX.test(draft.colorHex) ? draft.colorHex : '#000000'}
              disabled={disabled}
              onChange={(e) => onChange({ colorHex: e.target.value.toUpperCase() })}
            />
            <input
              type="text"
              className="selector fw"
              aria-label="Color en hexadecimal"
              maxLength={7}
              value={draft.colorHex}
              disabled={disabled}
              aria-invalid={errors.colorHex !== undefined}
              onChange={(e) => onChange({ colorHex: e.target.value })}
            />
          </span>
          {errors.colorHex && <span className="fld-err">{errors.colorHex}</span>}
        </label>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={draft.tiled}
        className={cn('tgl', !draft.tiled && 'off')}
        disabled={disabled}
        onClick={() => onChange({ tiled: !draft.tiled })}
      >
        <span className="sw" aria-hidden="true" />
        Repetir en mosaico
      </button>
    </fieldset>
  );
}
